import * as React from 'react';
import { FormEvent, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { deletePhoto, getCaseTypes, reportCase, TReportCaseRequest, uploadPhoto } from '../../../actions/CaseActions';
import { RouteComponentProps, withRouter } from 'react-router';
import { useTranslation } from 'react-i18next';
import { IAppState } from '../../../store/Store';
import {
  CaseType,
  DeleteCasePhoto,
  ReportCase,
  TApiCasePhoto,
  TCasesState,
  UploadCasePhoto,
} from '../../../types/Cases';
import { Theme } from '@mui/material/styles';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import CasePhotoList from '../../../components/cases/CasePhotoList';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import { MobileDatePicker } from '@mui/x-date-pickers/MobileDatePicker';
import { TDispatch } from '../../../types/Thunk';
import AlertBlock from '../../../components/AlertBlock';
import { TAuthState } from '../../../types/Auth';
import { useApp } from '../../../hooks/useApp';
import { routes } from '../../../Routes';
import CaseTypeSelect from '../../../components/cases/CaseTypeSelect';
import CasePhotoSelect from '../../../components/cases/CasePhotoSelect';
import { ContentPage } from '../../../types/Content';
import PageContent from '../../../components/PageContent';
import moment from 'moment';
import { setInspectionQuestion, setInspectionQuestionId, setInspectionSent } from '../../../actions/InspectionActions';
import { loadStoredRespondentState } from '../../../hooks/useCurrentActiveSurvey';
import { useInspection } from '../../../hooks/useInspection';
import { Alert, Autocomplete } from '@mui/material';
import { AlertColor } from '@mui/material/Alert';
import { useUser } from '../../../hooks/useUser';
import { Link } from 'react-router-dom';
import IconButton from '@mui/material/IconButton';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import Snackbar from '@mui/material/Snackbar';
import Paragraph from '../../../components/cms/Paragraph';
import { Divider, Slider } from '@mui/material';
import Typography from '@mui/material/Typography';
import FormHelperText from '@mui/material/FormHelperText';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    formControl: {
      width: '100%',
      marginTop: theme.typography.pxToRem(16),
      marginBottom: theme.typography.pxToRem(16),
    },
    submit: {
      margin: theme.spacing(3, 0, 3),
    },
    alertContainer: {
      width: '100%',
    },
    personalContainer: {
      border: '2px solid #6FADE7',
      padding: theme.spacing(2),
      width: '100%',
    },
    fileCopy: {
      padding: theme.spacing(0),
      margin: theme.spacing(0),
      textAlign: 'right',
    },
    title: {
      marginTop: '1em',
    },
    slider: {
      "& .MuiSlider-markLabel[data-index='0']": {
        transform: "translateX(0)",
        color: 'green !important',
      },
      "& .MuiSlider-markLabel[data-index='1']": {
        transform: "translateX(-100%)",
        color: 'red !important',
      }
    },
  })
);

const CaseReport: React.FC<RouteComponentProps<any>> = ({
  history,
}): JSX.Element => {
  const dispatch = useDispatch<TDispatch>();
  const { app } = useApp();
  const authState: TAuthState = useSelector<IAppState, TAuthState>(
    (state) => state.authState
  );
  const caseState: TCasesState = useSelector<IAppState, TCasesState>(
    (state: IAppState) => state.caseState
  );
  const { inspectionLocationId, isInspectionStored, inspectionQustionId } = useInspection();
  const classes = useStyles({});
  const { t } = useTranslation();
  const [type, setType] = useState<undefined | number>(undefined);
  const [workplaceReportUrl, setWorkplaceReportUrl] = useState('');
  const [problem, setProblem] = useState('');
  const [name, setName] = useState(authState.data?.user?.name as string);
  const [nearMissInjuredName, setNearMissInjuredName] = useState('');
  const [nearMissDate, setNearMissDate] = useState(moment());
  const [accidentInjuredName, setAccidentInjuredName] = useState('');
  const [accidentDate, setAccidentDate] = useState(moment());
  const [violenceInjuredName, setViolenceInjuredName] = useState('');
  const [violenceDate, setViolenceDate] = useState(moment());
  const [violencePhysicalReaction, setViolencePhysicalReaction] = useState<number>(0);
  const [violencePsychologicalReaction, setViolencePsychologicalReaction] = useState<number>(0);
  const [violenceWitness, setViolenceWitness] = useState('');
  const [violenceCauserOfDamage, setViolenceCauserOfDamage] = useState('');
  const [success, setSuccess] = useState(false);
  const [caseCode, setCaseCode] = useState<any>(null);
  const [errorOccurred, setErrorOccurred] = useState<any>(false);
  const [error, setError] = useState<any>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submitLock = useRef(false);
  const { getUserDepartments, getUserLocations, isImpersonated } = useUser();
  const userLocations = getUserLocations();
  const userDepartments = getUserDepartments();
  const [openSnack, setOpenSnack] = React.useState(false);
  const [snackType, setSnackType] = React.useState<AlertColor>('success');
  const [snackMessage, setSnackMessage] = React.useState('');

  const emptyValue = {
    id: 0,
    label: t('generic.none'),
  };

  const otherValue = {
    id: 0,
    label: t('generic.other'),
  };

  const marks = [
    {
      value: 0,
      label: t('case.violence.hardly_affected'),
    },
    {
      value: 10,
      label: t('case.violence.very_affected'),
    },
  ];

  const [location, setLocation] = useState(emptyValue);
  const [department, setDepartment] = useState(emptyValue);

  const [violenceCauserStatus, setViolenceCauserStatus] = useState(otherValue);
  const [violencePhysicalViolence, setViolencePhysicalViolence] = useState(otherValue);
  const [violencePsychologicalViolence, setViolencePsychologicalViolence] = useState(otherValue);
  const [violenceTrigger, setViolenceTrigger] = useState(otherValue);
  const [violenceDuration, setViolenceDuration] = useState(otherValue);

  const [locationsList, setLocationsList] = useState([emptyValue]);
  const [departmentsList, setDepartmentsList] = useState([emptyValue]);
  const [permittedLocationsList, setPermittedLocationsList] = useState([emptyValue]);
  const [permittedDepartmentsList, setPermittedDepartmentsList] = useState([emptyValue]);

  const filterByPermissions =
    caseState.data?.find((caseType) => caseType.type === type)
      ?.filterByPermissions ?? false;

  const activeLocationsList = filterByPermissions
    ? permittedLocationsList
    : locationsList;
  const activeDepartmentsList = filterByPermissions
    ? permittedDepartmentsList
    : departmentsList;

  const [violenceCauserStatusList, setViolenceCauserStatusList] = useState([otherValue]);
  const [violencePhysicalViolenceList, setViolencePhysicalViolenceList] = useState([otherValue]);
  const [violencePsychologicalViolenceList, setViolencePsychologicalViolenceList] = useState([otherValue]);
  const [violenceTriggerList, setViolenceTriggerList] = useState([otherValue]);
  const [violenceDurationList, setViolenceDurationList] = useState([otherValue]);

  const [accidentIncapacityForWork, setAccidentIncapacityForWork] = useState(emptyValue);
  const [accidentIncapacityList, setAccidentIncapacityList] = useState([emptyValue]);

  useEffect(() => {
    if (userDepartments?.length > 0) {
      setDepartment({
        id: userDepartments[0].id,
        label: userDepartments[0]?.title,
      });
    }

    let inspectionLocationWasChosen = false;

    if (
      isInspectionStored &&
      inspectionLocationId > 0 &&
      locationsList.length > 0
    ) {
      const chosenLocation = locationsList.find((l) => {
        return l.id === inspectionLocationId;
      });

      if (chosenLocation) {
        setLocation(chosenLocation);
        inspectionLocationWasChosen = true;
      }
    }
    if (!inspectionLocationWasChosen && userLocations?.length > 0) {
      setLocation({
        id: userLocations[0].id,
        label: userLocations[0]?.title,
      });
    }
  }, [
    userLocations,
    userDepartments,
    isInspectionStored,
    inspectionLocationId,
    locationsList,
  ]);

  useEffect(
    () => () => {
      dispatch(setInspectionQuestion(false));
      dispatch(setInspectionQuestionId(0));
    },
    //eslint-disable-next-line
    []
  );

  useEffect(() => {
    if (caseState.variables) {
      setViolenceCauserStatusList([...caseState.variables.violenceCauseStatus, otherValue]);
      setViolencePhysicalViolenceList([...caseState.variables.violencePhysicalViolence, otherValue]);
      setViolencePsychologicalViolenceList([...caseState.variables.violencePsychologicalViolence, otherValue]);
      setViolenceTriggerList([...caseState.variables.violenceTrigger, otherValue]);
      setViolenceDurationList([...caseState.variables.violenceDuration, otherValue]);

      const incapacityOptions = caseState.variables.accidentIncapacityForWork ?? [];
      setAccidentIncapacityList(incapacityOptions);
      if (incapacityOptions.length > 0) {
        setAccidentIncapacityForWork(incapacityOptions[0]);
      }
    }

    if (caseState.locations) {
      const arr = caseState.locations.map((element: any) => {
        return {
          id: element.entity.id,
          label: '-'.repeat(element.depth) + ' ' + element.entity.title,
        };
      });

      arr.unshift(emptyValue);
      setLocationsList(arr);
    }

    if (caseState.permittedLocations) {
      const arr = caseState.permittedLocations.map((element: any) => {
        return {
          id: element.entity.id,
          label: element.entity.title,
        };
      });

      arr.unshift(emptyValue);
      setPermittedLocationsList(arr);
    }

    if (caseState.departments) {
      const arr = caseState.departments.map((element: any) => {

        return {
          id: element.entity.id,
          label: '-'.repeat(element.depth) + ' ' + element.entity.title,
        };
      });

      arr.unshift(emptyValue);
      setDepartmentsList(arr);
    }

    if (caseState.permittedDepartments) {
      const arr = caseState.permittedDepartments.map((element: any) => {
        return {
          id: element.entity.id,
          label: element.entity.title,
        };
      });

      arr.unshift(emptyValue);
      setPermittedDepartmentsList(arr);
    }

    if (caseState.data && caseState.data.length > 0) {
      if (!workplaceReportUrl) {
        caseState.data.forEach(function (e) {
          if (e.type === CaseType.WORKPLACE_ACCIDENT && e.reportUrl) {
            setWorkplaceReportUrl(e.reportUrl);
          }
        });
      }
    }
    //eslint-disable-next-line
  }, [
    caseState.data,
    caseState.locations,
    caseState.departments,
    caseState.permittedLocations,
    caseState.permittedDepartments,
    caseState.variables,
    workplaceReportUrl,
  ]);

  useEffect(() => {
    if (!filterByPermissions) {
      return;
    }

    if (
      location.id !== 0 &&
      !activeLocationsList.some((option) => option.id === location.id)
    ) {
      setLocation(emptyValue);
    }

    if (
      department.id !== 0 &&
      !activeDepartmentsList.some((option) => option.id === department.id)
    ) {
      setDepartment(emptyValue);
    }
    //eslint-disable-next-line
  }, [filterByPermissions, activeLocationsList, activeDepartmentsList]);

  const deleteUploadedPhoto = async (photoId: number) => {
    await dispatch(deletePhoto(photoId)).catch(() => {
      setErrorOccurred(true);
    });
  };

  const handlePhotoUpload = async (fileName: string, base64Content: string) => {
    await dispatch(uploadPhoto(fileName, base64Content)).catch(() => {
      setErrorOccurred(true);
    });
  };

  useEffect(() => {
    if (app.hasError(UploadCasePhoto)) {
      setError(app.getError(UploadCasePhoto));
    }
    if (app.hasError(DeleteCasePhoto)) {
      setError(app.getError(DeleteCasePhoto));
    }
    if (app.hasError(ReportCase)) {
      setError(app.getError(ReportCase));
    }
  }, [app]);

  const isNearMiss = type === CaseType.NEAR_MISS_ACCIDENT;
  const isWhistleblower = type === CaseType.WHISTLEBLOWER;
  const accidentType = caseState.data?.find(
    (e) => e.type === CaseType.WORKPLACE_ACCIDENT
  );
  const isWorkplaceAccident = type === CaseType.WORKPLACE_ACCIDENT;
  const isEasyAccident = isWorkplaceAccident && accidentType?.appForm === 'easy';
  const isOshureAccident =
    isWorkplaceAccident && accidentType?.appForm === 'oshure';
  const isViolence = type === CaseType.VIOLENCE_ACCIDENT;

  const submitWorkplaceReport = () => {
    const newWindow = window.open(
      workplaceReportUrl,
      '_blank',
      'noopener,noreferrer'
    );
    if (newWindow) newWindow.opener = null;
  };

  const submitCaseReport = async (e: FormEvent) => {
    e.preventDefault();

    if (submitLock.current) {
      return;
    }
    submitLock.current = true;
    setIsSubmitting(true);

    const photos: Array<number> = [];
    if (caseState.photos && caseState.photos.length > 0) {
      caseState.photos.forEach((photo: TApiCasePhoto) => {
        return photos.push(photo.id);
      });
    }

    const requestData: TReportCaseRequest = {
      category: type as number,
      problemDescription: problem,
      authorName: isWhistleblower ? 'Anonymous' : name,
      photos,
      nearMissInjuredName: isWhistleblower ? '' : nearMissInjuredName,
      nearMissDate: isWhistleblower ? '' : nearMissDate?.format('YYYY-MM-DD'),
      location: isWhistleblower ? 0 : location.id,
      department: isWhistleblower ? 0 : department.id,
      violenceInjuredName: isWhistleblower ? '' : violenceInjuredName,
      violenceDate: isWhistleblower ? '' : violenceDate?.format('YYYY-MM-DD'),
      violencePhysicalReaction: isWhistleblower ? 0 : violencePhysicalReaction,
      violencePsychologicalReaction: isWhistleblower ? 0 : violencePsychologicalReaction,
      violenceWitness: isWhistleblower ? '' : violenceWitness,
      violenceCauserOfDamage: isWhistleblower ? '' : violenceCauserOfDamage,
      violenceCauserStatus: isWhistleblower ? 0 : violenceCauserStatus.id,
      violencePhysicalViolence: isWhistleblower ? 0 : violencePhysicalViolence.id,
      violencePsychologicalViolence: isWhistleblower ? 0 : violencePsychologicalViolence.id,
      violenceTrigger: isWhistleblower ? 0 : violenceTrigger.id,
      violenceDuration: isWhistleblower ? 0 : violenceDuration.id,
      accidentInjuredName: isWhistleblower ? '' : accidentInjuredName,
      accidentDate: isWhistleblower ? '' : accidentDate?.format('YYYY-MM-DD'),
      accidentIncapacityForWork: accidentIncapacityForWork.id || null,
    };

    if (isInspectionStored) {
      const respondentIdentifier = loadStoredRespondentState();
      if (respondentIdentifier) {
        requestData.respondentId = respondentIdentifier.id;
      }
      if (inspectionQustionId > 0) {
        requestData.questionId = inspectionQustionId;
      }
    }

    await dispatch(reportCase(requestData))
      .then((createData) => {
        setProblem('');
        setType(1);
        setError(null);
        setCaseCode(createData.data.code);
        //If we need to go back, we shouldn't display success message here.
        if (isInspectionStored) {
          dispatch(setInspectionSent(true));
          dispatch(setInspectionQuestionId(0));
          history.goBack();
        } else {
          setSuccess(true);
        }
      })
      .catch(() => {
        setSuccess(false);
        setErrorOccurred(true);
      })
      .finally(() => {
        submitLock.current = false;
        setIsSubmitting(false);
        window.scrollTo(0, 0);
      });
  };

  const fetchCaseTypes = async () => {
    await dispatch(getCaseTypes(authState.settings?.languageCode));
  };

  const copyToClipboard = (code : string) => {
    const whistleblowerLink = `${window.location.origin}${routes.prefix}${routes.whistleblowerCommunication}`;
    navigator.clipboard.writeText(`
      ${t('whistleblower.report.success.link')}
      ${whistleblowerLink}
      ${t('whistleblower.report.success.personal_code')} ${code}
    `).then(
      () => {
        setSnackMessage(t('whistleblower.report.copy_to_clipboard.success'));
        setOpenSnack(true);
      })
      .catch((e) => {
        setSnackType('error');
        setSnackMessage(t('whistleblower.report.copy_to_clipboard.error'));
        setOpenSnack(true);

        throw e;
      });
  }

  useEffect(() => {
    if (authState.settings?.languageCode) {
      fetchCaseTypes();
    }
    //eslint-disable-next-line
  }, [dispatch, authState.settings]);

  useEffect(() => {
    if (!caseState.loading && caseState.data && caseState.data.length < 1) {
      history.push(routes.home);
    }
  });

  return (
    <>
      {success && (
        <AlertBlock type={'success'} onClose={() => setSuccess(false)}>
          {t('case.report.success.alert')}
        </AlertBlock>
      )}
      {success && caseCode && (
        <>
          <AlertBlock type='info'>
            <Paragraph body={t('case.whistleblower.success_message_2')} />
            <p className={classes.personalContainer}>
              <p>
                <b>{t('whistleblower.report.success.link')}</b>:{' '}
                <Link to={routes.whistleblowerCommunication} target='_blank'>
                  {`${window.location.origin}${routes.prefix}${routes.whistleblowerCommunication}`}
                </Link>
              </p>
              <p>
                <b>{t('whistleblower.report.success.personal_code')}</b>:{' '}
                {caseCode}
              </p>
              <p className={classes.fileCopy}>
                <IconButton
                  aria-label='copy'
                  onClick={() => copyToClipboard(caseCode as string)}
                  size="large">
                  <FileCopyIcon
                    color="primary"
                  />
                </IconButton>
              </p>
            </p>
          </AlertBlock>
          <Snackbar
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            open={openSnack}
            autoHideDuration={6000}
            onClose={() => setOpenSnack(false)}
          >
            <Alert severity={snackType} onClose={() => setOpenSnack(false)}>
              {snackMessage}
            </Alert>
          </Snackbar>
        </>
      )}
      {error && errorOccurred && (
        <AlertBlock type={'error'}>{error?.message}</AlertBlock>
      )}
      {caseState.enabled === false && (
        <AlertBlock hideCloseButton={true} type={'warning'}>
          {t('case.report.disabled')}
        </AlertBlock>
      )}
      {caseState.enabled === true && (
        <form onSubmit={submitCaseReport}>
          <PageContent
            pageType={ContentPage.CASES_PAGE}
            showAsParagraph={true}
          />
          <CaseTypeSelect caseState={caseState} type={type} setType={setType} />

          {isEasyAccident ? (
            <>
              <AlertBlock type={'info'} hideCloseButton={true}>
                {t('case.workplace_accident_info')}
              </AlertBlock>

              {!isImpersonated() && (
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <Button
                      type='button'
                      fullWidth
                      variant='contained'
                      color='primary'
                      className={classes.submit}
                      onClick={submitWorkplaceReport}
                    >
                      {t('case.report_accident')}
                    </Button>
                  </Grid>
                </Grid>
              )}
            </>
          ) : (
            <>
              {isWhistleblower && (
                <AlertBlock type={'info'} hideCloseButton={true}>
                  {t('case.whistleblower_info_1')}
                  <br />
                  <br />
                  {t('case.whistleblower_info_2')}
                </AlertBlock>
              )}

              {!isImpersonated() && (
                <>
                  <CasePhotoSelect handlePhotoUpload={handlePhotoUpload} accept='*' />
                  <FormHelperText variant="filled">
                    {t('case.report.media_helptext')}
                  </FormHelperText>

                  {caseState.photos && caseState.photos.length > 0 && (
                    <CasePhotoList
                      photos={caseState.photos}
                      deleteUploadedPhoto={deleteUploadedPhoto}
                    />
                  )}
                </>
              )}

              <TextField
                variant='outlined'
                margin='normal'
                fullWidth
                name='password'
                label={` ${t(
                  isNearMiss
                    ? 'case.near_miss.describe.title'
                    : isOshureAccident
                    ? 'case.accident.description.title'
                    : 'case.problem.label'
                )} *`}
                type='text'
                id='problem'
                multiline
                rows={3}
                value={problem}
                error={!!error?.fields?.problemDescription}
                helperText={
                  error?.fields?.problemDescription ??
                  (isNearMiss
                    ? t('case.near_miss.describe.label')
                    : isOshureAccident
                    ? t('case.accident.description.label')
                    : null)
                }
                onChange={(e) => setProblem(e.target.value)}
              />

              {isNearMiss && (
                <>
                  <TextField
                    variant='outlined'
                    margin='normal'
                    fullWidth
                    label={`${t('case.near_miss.injured_name.title')} *`}
                    type='text'
                    value={nearMissInjuredName}
                    error={!!error?.fields?.nearMissInjuredName}
                    helperText={
                      error?.fields?.nearMissInjuredName ??
                      t('case.near_miss.injured_name.label')
                    }
                    onChange={(e) => setNearMissInjuredName(e.target.value)}
                  />
                  <MobileDatePicker
                    disableFuture
                    format='YYYY-MM-DD'
                    value={nearMissDate}
                    onChange={(e) => {
                      if (e) {
                        setNearMissDate(e);
                      }
                    }}
                    label={`${t('case.near_miss.date.title')} *`}
                    slotProps={{
                      textField: {
                        variant: 'outlined',
                        margin: 'normal',
                        fullWidth: true,
                        error: !!error?.fields?.nearMissDate,
                        helperText:
                          error?.fields?.nearMissDate ??
                          t('case.near_miss.date.label'),
                      },
                      actionBar: { actions: ['today', 'accept'] },
                    }}
                  />
                </>
              )}

              {isOshureAccident && (
                <>
                  <TextField
                    variant='outlined'
                    margin='normal'
                    fullWidth
                    label={`${t('case.accident.injured_name.title')} *`}
                    type='text'
                    value={accidentInjuredName}
                    error={!!error?.fields?.accidentInjuredName}
                    helperText={
                      error?.fields?.accidentInjuredName ??
                      t('case.accident.injured_name.label')
                    }
                    onChange={(e) => setAccidentInjuredName(e.target.value)}
                  />
                  <MobileDatePicker
                    disableFuture
                    format='YYYY-MM-DD'
                    value={accidentDate}
                    onChange={(e) => {
                      if (e) {
                        setAccidentDate(e);
                      }
                    }}
                    label={`${t('case.accident.date.title')} *`}
                    slotProps={{
                      textField: {
                        variant: 'outlined',
                        margin: 'normal',
                        fullWidth: true,
                        error: !!error?.fields?.accidentDate,
                        helperText:
                          error?.fields?.accidentDate ??
                          t('case.accident.date.label'),
                      },
                      actionBar: { actions: ['today', 'accept'] },
                    }}
                  />
                  <Autocomplete
                    options={accidentIncapacityList}
                    getOptionLabel={(option) => option.label}
                    value={accidentIncapacityForWork}
                    onChange={(event: any, newValue: any) => {
                      setAccidentIncapacityForWork(newValue ?? emptyValue);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant='outlined'
                        margin='normal'
                        fullWidth
                        label={t('case.accident.incapacity.title')}
                        helperText={t('case.accident.incapacity.label')}
                      />
                    )}
                  />
                </>
              )}

              {isViolence && (
                <>
                  <TextField
                    variant='outlined'
                    margin='normal'
                    fullWidth
                    label={`${t('case.violence.injured_name.title')} *`}
                    type='text'
                    value={violenceInjuredName}
                    error={!!error?.fields?.violenceInjuredName}
                    helperText={
                      error?.fields?.violenceInjuredName ??
                      t('case.violence.injured_name.label')
                    }
                    onChange={(e) => setViolenceInjuredName(e.target.value)}
                  />
                  <MobileDatePicker
                    disableFuture
                    format='YYYY-MM-DD'
                    value={violenceDate}
                    onChange={(e) => {
                      if (e) {
                        setViolenceDate(e);
                      }
                    }}
                    label={`${t('case.violence.date.title')} *`}
                    slotProps={{
                      textField: {
                        variant: 'outlined',
                        margin: 'normal',
                        fullWidth: true,
                        error: !!error?.fields?.violenceDate,
                        helperText:
                          error?.fields?.violenceDate ??
                          t('case.violence.date.label'),
                      },
                      actionBar: { actions: ['today', 'accept'] },
                    }}
                  />

                  <Typography id="physical-reaction-slider">
                    {t('case.violence.physical_reaction')}
                  </Typography>
                  <Slider
                    value={violencePhysicalReaction}
                    aria-labelledby="physical-reaction-slider"
                    classes={{ root: classes.slider }}
                    onChange={(event, newValue) => setViolencePhysicalReaction(newValue as number)}
                    min={0}
                    max={10}
                    step={1}
                    valueLabelDisplay="auto"
                    marks={marks}
                  />

                  <Typography className={classes.title} id="psychological-reaction-slider">
                    {t('case.violence.psychological_reaction')}
                  </Typography>
                  <Slider
                    value={violencePsychologicalReaction}
                    aria-labelledby="psychological-reaction-slider"
                    classes={{ root: classes.slider }}
                    onChange={(event, newValue) => setViolencePsychologicalReaction(newValue as number)}
                    min={0}
                    max={10}
                    step={1}
                    valueLabelDisplay="auto"
                    marks={marks}
                  />
                  <TextField
                    variant='outlined'
                    margin='normal'
                    fullWidth
                    label={t('case.violence.witness.title')}
                    type='text'
                    value={violenceWitness}
                    helperText={t('case.violence.witness.label')}
                    onChange={(e) => setViolenceWitness(e.target.value)}
                  />
                  <Divider />
                  <TextField
                    variant='outlined'
                    margin='normal'
                    fullWidth
                    label={t('case.violence.causer_of_damage')}
                    type='text'
                    value={violenceCauserOfDamage}
                    onChange={(e) => setViolenceCauserOfDamage(e.target.value)}
                  />
                  <Autocomplete
                    options={violenceCauserStatusList}
                    getOptionLabel={(option) => option.label}
                    value={violenceCauserStatus}
                    onChange={(event: any, newValue: any) => {
                      setViolenceCauserStatus(newValue ?? otherValue);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant='outlined'
                        margin='normal'
                        fullWidth
                        label={t('case.violence.causer_status')}
                      />
                    )}
                  />
                  <Divider />
                  <Autocomplete
                    options={violencePhysicalViolenceList}
                    getOptionLabel={(option) => option.label}
                    value={violencePhysicalViolence}
                    onChange={(event: any, newValue: any) => {
                      setViolencePhysicalViolence(newValue ?? otherValue);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant='outlined'
                        margin='normal'
                        fullWidth
                        label={t('case.violence.physical_violence.title')}
                        helperText={t('case.violence.physical_violence.label')}
                      />
                    )}
                  />
                  <Autocomplete
                    options={violencePsychologicalViolenceList}
                    getOptionLabel={(option) => option.label}
                    value={violencePsychologicalViolence}
                    onChange={(event: any, newValue: any) => {
                      setViolencePsychologicalViolence(newValue ?? otherValue);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant='outlined'
                        margin='normal'
                        fullWidth
                        label={t('case.violence.psychological_violence.title')}
                        helperText={t('case.violence.psychological_violence.label')}
                      />
                    )}
                  />
                  <Divider />
                  <Autocomplete
                    options={violenceTriggerList}
                    getOptionLabel={(option) => option.label}
                    value={violenceTrigger}
                    onChange={(event: any, newValue: any) => {
                      setViolenceTrigger(newValue ?? otherValue);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant='outlined'
                        margin='normal'
                        fullWidth
                        label={t('case.violence.trigger.title')}
                        helperText={t('case.violence.trigger.label')}
                      />
                    )}
                  />
                  <Autocomplete
                    options={violenceDurationList}
                    getOptionLabel={(option) => option.label}
                    value={violenceDuration}
                    onChange={(event: any, newValue: any) => {
                      setViolenceDuration(newValue ?? otherValue);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant='outlined'
                        margin='normal'
                        fullWidth
                        label={t('case.violence.duration.title')}
                        helperText={t('case.violence.duration.label')}
                      />
                    )}
                  />
                  <Divider />
                </>
              )}

              {!isWhistleblower && (
                <>
                  <Autocomplete
                    options={activeLocationsList}
                    getOptionLabel={(option) => option.label}
                    value={location}
                    onChange={(event: any, newValue: any) => {
                      setLocation(newValue ?? emptyValue);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant='outlined'
                        margin='normal'
                        fullWidth
                        label={`${t('case.location.label')}`}
                        error={!!error?.fields?.location}
                        helperText={
                          error?.fields?.location ?? t('case.location.helper')
                        }
                      />
                    )}
                  />

                  <Autocomplete
                    options={activeDepartmentsList}
                    getOptionLabel={(option) => option.label}
                    value={department}
                    onChange={(event: any, newValue: any) => {
                      setDepartment(newValue ?? emptyValue);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant='outlined'
                        margin='normal'
                        fullWidth
                        label={`${t('case.department.label')}`}
                        error={!!error?.fields?.department}
                        helperText={
                          error?.fields?.department ??
                          t('case.department.helper')
                        }
                      />
                    )}
                  />

                  <TextField
                    variant='outlined'
                    margin='normal'
                    fullWidth
                    name='name'
                    label={`${t('case.name.label')} *`}
                    type='text'
                    id='name'
                    value={name}
                    error={!!error?.fields?.authorName}
                    helperText={
                      error?.fields?.authorName ??
                      (isNearMiss ? t('case.near_miss.name.label') : null)
                    }
                    onChange={(e) => setName(e.target.value)}
                  />
                </>
              )}

              {!isImpersonated() && (
                <Grid container spacing={1} justifyContent="space-around">
                  <Grid item xs={6}>
                    <Button
                      type='submit'
                      fullWidth
                      variant='contained'
                      color='primary'
                      className={classes.submit}
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <CircularProgress size={24} color='inherit' />
                      ) : (
                        <span>{t('case.report.submit.button')}</span>
                      )}
                    </Button>
                  </Grid>
                </Grid>
              )}
            </>
          )}
        </form>
      )}
    </>
  );
};

export default withRouter(CaseReport);
